import {
    type Attributes,
    type ComponentProps,
    type ComponentType,
    type FC,
    type ForwardedRef,
    type HTMLAttributes,
    type ReactElement,
    createElement
} from "react"

import eq from "lodash-es/eq"
import { ensuredForwardRef } from "react-use"

import { buildCssClassName, getTransitionTwClasses, isFirefoxBrowser } from "@/3514/utils"

import { useModulesExcerptsList } from "../hooks"
import {
    EParticipantHeaderComponentLayout as EComponentLayout,
    EParticipantHeaderComponentName as EComponentName,
    type TParticipantHeaderComponentHelperProps as THelperProps,
    participantHeaderUiConfig
} from "../ui"

type TComponentRendererProps<TComponentProps> = {
    layout?: EComponentLayout
    componentName: EComponentName
    componentProps?: TComponentProps
    component: ComponentType
}

/**
 * HOC renderer for the ParticipantHeader' entities
 * @param layout EParticipantHeaderComponentLayout
 * @param componentName EParticipantHeaderComponentName
 * @param componentProps TComponentProps
 * @param Component ComponentType
 * @return {Component}
 */
const ComponentRenderer: <TComponentProps>(props: TComponentRendererProps<TComponentProps>) => ReactElement = <
    TComponentProps,
>({
    layout = EComponentLayout.Page,
    componentName,
    componentProps,
    component: Component
}: TComponentRendererProps<TComponentProps>): ReactElement => (
    <Component
        {...({
            ...componentProps,
            ...participantHeaderUiConfig.headerComponentsRenderConfig[componentName][layout]()
        } as ComponentProps<typeof Component>)}
    />
)

const _sharedAnalyticsTitleWrapperClasses: string = "pb-[8px] border-b-[1px] border-gray-500"
const _sharedAnalyticsComponentWrapperClasses: string = "flex flex-col"

const _ARE_ACHIEVEMENTS_HOVERABLE: boolean = false

const classes = {
    achievementsComponent: (layout: EComponentLayout): string =>
        buildCssClassName(
            eq(layout, EComponentLayout.Page)
                ? [
                      "mt-0",
                      "mb-auto",
                      "flex",
                      "flex-col",
                      "py-[5px]",
                      "justify-between",
                      "bg-white",
                      _ARE_ACHIEVEMENTS_HOVERABLE && [
                          "hover:border-blue-400",
                          "focus:border-blue-400",
                          "hover:rounded-[5px]",
                          "focus:rounded-[5px]",
                          "hover:bg-blue-600",
                          "focus:bg-blue-600",
                          getTransitionTwClasses("all", 100)
                      ]
                  ]
                : _sharedAnalyticsComponentWrapperClasses
        ),
    achievementsContent: (layout: EComponentLayout): string =>
        buildCssClassName(
            eq(layout, EComponentLayout.Page)
                ? "w-max inline-grid grid-cols-4"
                : [
                      "flex",
                      "flex-wrap",
                      "justify-center",
                      "md:grid",
                      "md:!grid-cols-2",
                      "*:basis-[32%]",
                      "!gap-[5px]",
                      "my-[15px]",
                      "w-full"
                  ]
        ),
    modulesComponent: (layout: EComponentLayout): string =>
        buildCssClassName(
            eq(layout, EComponentLayout.Page)
                ? [
                      "bg-gray-500",
                      "md:bg-white",
                      "md:ml-[10px]",
                      "rounded-[15px]",
                      "md:rounded-[0px]",
                      "md:min-w-[170px]",
                      "max-w-full",
                      "xl:max-w-[365px]",
                      "w-fit",
                      "md:w-auto",
                      "mt-0",
                      "mb-auto",
                      "relative",
                      "flex",
                      "flex-col",
                      "md:translate-y-[-1px]"
                  ]
                : _sharedAnalyticsComponentWrapperClasses
        ),
    modulesShadow: (): string =>
        buildCssClassName(
            "hidden",
            "lg:block",
            "z-[1]",
            "absolute",
            "right-0",
            "top-0",
            "h-[90%]",
            "w-[30px]",
            "bg-gradient-to-l",
            "from-white",
            "to-transparent",
            "pointer-events-none"
        ),
    modulesContent: (
        layout: EComponentLayout,
        { isScrollable, isFirefox }: { isScrollable: boolean; isFirefox: boolean }
    ): string =>
        buildCssClassName(
            eq(layout, EComponentLayout.Page)
                ? [
                      "flex grow group overflow-y-hidden gap-x-[7px] md:gap-x-[0px]",
                      isScrollable
                          ? ["overflow-x-scroll scrollbar-module-excerpts", isFirefox ? "pb-[8px]" : "pb-[1px]"]
                          : ["no-scrollbar md:pb-[9px]", isFirefox ? "pb-[8px]" : "pb-[0px]"]
                  ]
                : [
                      "flex",
                      "items-start",
                      "gap-x-[7px]",
                      "overflow-y-scroll",
                      "max-h-[70vh]",
                      "no-scrollbar",
                      "flex-col",
                      "mt-auto",
                      "group"
                  ]
        ),
    sessionTitle: (layout: EComponentLayout): string =>
        buildCssClassName(eq(layout, EComponentLayout.Page) ? "" : _sharedAnalyticsTitleWrapperClasses),
    sessionComponent: (layout: EComponentLayout): string =>
        buildCssClassName(
            eq(layout, EComponentLayout.Page)
                ? [
                      "bg-lightblue-400",
                      "cursor-pointer",
                      "flex",
                      "flex-row",
                      "md:flex-col",
                      "items-center",
                      "px-[15px]",
                      "pt-[8px]",
                      "pb-[12px]",
                      "md:rounded-[10px]",
                      "md:min-w-[130px]",
                      "md:w-[130px]",
                      "rounded-[15px]",
                      "w-full",
                      "md:min-h-[165px]",
                      "md:max-h-[165px]",
                      "md:mb-auto",
                      "outline-[2px]",
                      "outline",
                      "outline-transparent",
                      "mt-[2px]",
                      "md:mt-0",
                      "hover:bg-lightblue-500",
                      "focus:bg-lightblue-500",
                      "hover:outline-lightblue-300",
                      "focus:outline-lightblue-300",
                      getTransitionTwClasses("[outline-color,background-color]", 200)
                  ]
                : _sharedAnalyticsComponentWrapperClasses
        ),
    sessionContent: (layout: EComponentLayout): string =>
        buildCssClassName(
            eq(layout, EComponentLayout.Page)
                ? [
                      "w-full",
                      "flex",
                      "flex-row",
                      "items-center",
                      "justify-start",
                      "gap-x-[12px]",
                      "md:gap-x-[0px]",
                      "md:gap-y-[10px]",
                      "md:!flex-col"
                  ]
                : [
                      "mt-[15px]",
                      "px-[15px]",
                      "py-[10px]",
                      "bg-lightblue-400",
                      "rounded-[5px]",
                      "flex",
                      "items-center",
                      "flex-row-reverse",
                      "justify-center",
                      "h-min"
                  ]
        )
} as const

const journeyAchievementsComponentWrapper: FC<THelperProps> = ({
    children,
    layout = EComponentLayout.Page
}: THelperProps): ReactElement => <article className={classes.achievementsComponent(layout)}>{children}</article>

const journeyAchievementsContentWrapper: FC<THelperProps> = ({
    children,
    layout = EComponentLayout.Page
}: THelperProps): ReactElement => <div className={classes.achievementsContent(layout)}>{children}</div>

const moduleExcerptsComponentWrapper: FC<THelperProps> = ({
    children,
    onMouseLeave,
    onMouseEnter,
    layout = EComponentLayout.Page,
    isScrolledToEnd = false
}: THelperProps & { isScrolledToEnd?: boolean }): ReactElement => (
    <article onMouseLeave={onMouseLeave} onMouseEnter={onMouseEnter} className={classes.modulesComponent(layout)}>
        {isScrolledToEnd || <div className={classes.modulesShadow()} />}
        {children}
    </article>
)

const moduleExcerptsContentWrapper: FC<THelperProps> = ensuredForwardRef(
    (
        {
            children,
            layout = EComponentLayout.Page,
            onClick,
            onKeyDown,
            role,
            isActionAffected = false
        }: THelperProps & HTMLAttributes<HTMLDivElement> & { isActionAffected?: boolean },
        ref: ForwardedRef<HTMLDivElement>
    ): ReactElement => {
        const { isScrollable }: ReturnType<typeof useModulesExcerptsList> = useModulesExcerptsList()

        return createElement(
            "div",
            {
                role,
                onClick,
                onKeyDown,
                className: classes.modulesContent(layout, {
                    isScrollable: isActionAffected ? isScrollable : false,
                    isFirefox: isFirefoxBrowser()
                }),
                ...(eq(layout, EComponentLayout.Page) && { ref })
            },
            children
        )
    }
)
moduleExcerptsContentWrapper.displayName = "moduleExcerptsContentWrapper"

const nextSessionTitleWrapper: FC<THelperProps> = ({
    children,
    layout = EComponentLayout.Page
}: THelperProps): ReactElement => <div className={classes.sessionTitle(layout)}>{children}</div>

const nextSessionComponentWrapper: FC<THelperProps & HTMLAttributes<HTMLDivElement>> = ({
    children,
    onClick,
    onKeyDown,
    role,
    tabIndex,
    layout = EComponentLayout.Page
}: THelperProps & HTMLAttributes<HTMLDivElement>): ReactElement =>
    createElement(
        eq(layout, EComponentLayout.Page) ? "article" : "div",
        { className: classes.sessionComponent(layout), onClick, onKeyDown, role, tabIndex },
        children
    )

const nextSessionContentWrapper: FC<THelperProps> = ({
    children,
    layout = EComponentLayout.Page
}: THelperProps): ReactElement =>
    createElement("div", { className: classes.sessionContent(layout) } as Attributes, children)

const helpers: {
    componentRenderer: typeof ComponentRenderer
    journeyAchievementsComponentWrapper: typeof journeyAchievementsComponentWrapper
    journeyAchievementsContentWrapper: typeof journeyAchievementsContentWrapper
    moduleExcerptsComponentWrapper: typeof moduleExcerptsComponentWrapper
    moduleExcerptsContentWrapper: typeof moduleExcerptsContentWrapper
    nextSessionTitleWrapper: typeof nextSessionTitleWrapper
    nextSessionComponentWrapper: typeof nextSessionComponentWrapper
    nextSessionContentWrapper: typeof nextSessionContentWrapper
} = {
    componentRenderer: ComponentRenderer,
    journeyAchievementsComponentWrapper,
    journeyAchievementsContentWrapper,
    moduleExcerptsComponentWrapper,
    moduleExcerptsContentWrapper,
    nextSessionTitleWrapper,
    nextSessionComponentWrapper,
    nextSessionContentWrapper
}

export { helpers as participantHeaderHelperComponents }
